<template>
  <div data-view="users">
    <router-view />

    <layout-logged-frame
      :title="$t('internal_users')"
      :total-count="pagination.totalItems"
    >
      <template v-slot:header-actions>
        <Can action="store" subject="user">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'internal-users-new' })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_user') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:content>
        <UsersFilters v-if="hasFilters" @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="users"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectUser"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'name'">
              {{ getUserName(scope.cell.item) }}
            </template>

            <template v-if="scope.cell.column.value === 'status.name'">
              <StatusPanel
                table-group="users"
                :status="scope.cell.item.status.name"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'role.name'">
              {{ displayRoleColumn(scope.cell.item) }}
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import UsersFilters from '../../components/forms/filters/InternalUsers';
  import StatusPanel from '../../components/panels/Status';
  import {extractNestedProp, isNum, isStr} from '../../utils';
  import columns from '../../config/users/columnsInternal';

  export default {
    components: {
      UsersFilters,
      StatusPanel,
    },
    data() {
      return {
        columns,
        pagination: {
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          order: 'desc' ,
          totalItems: 0,
        },
        totalPages: 0,
        query: {},
        isLoading: false,
      };
    },
    computed: {
      users() {
        return this.$store.getters['users/internalItems'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      hasFilters() {
        return (
          this.$ability.can('see-user-status', 'filter') ||
          this.$ability.can('see-user-role', 'filter') ||
          this.$ability.can('see-user-search', 'filter') ||
          this.$ability.can('see-user-business', 'filter')
        );
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.query?.status === 'success' &&
          (from.name === 'internal-users-new' || from.name === 'internal-users-edit') &&
          to.name === 'internal-users'
        ) {
          this.getUsers(this.query);
        }
      },
    },
    methods: {
      getUserName(user) {
        const { firstname, lastname } = user;
        return `${firstname} ${lastname}`;
      },
      getUsers({ page = 1, order = 'asc', sort = '', search = '', role = '', businessId = -1, status = '', countries} = {}) {
        const query = {
          page,
          order,
          sort,
          search,
          role,
          businessId,
          status,
          countries
        };

        this.isLoading = true;
        this.pagination.order = order;

        return this.$store
          .dispatch('users/getInternalItems', query)
          .then((res) => {
            const itemsMeta = this.$store.getters['users/internalItemsMeta'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectUser(user) {
        this.$store.dispatch('users/setActiveItem', user);
        this.$router.push({ name: 'internal-users-edit', params: { id: user.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.getUsers(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getUsers(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...this.getSearchQuery(filters)
        };

        this.getUsers(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = {sort: sortBy};

        this.getUsers(this.query);
      },
      getSearchQuery({ search, business, role, status, countries }) {
        const query = {};

        query.search = search;
        query.businessId = isNum(business) ? business : -1;
        query.role = isStr(role) ? role : '';
        query.status = isNum(status) ? status : '';
        query.countries = countries;

        return query;
      },
      displayRoleColumn(item) {
        return item.role.name;
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

.v-datatable {
  min-width: $table-min-width;
}
</style>
